import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import TruncatedTooltip from '@databases/atlaskit/TruncatedTooltip';
import SelectFieldMenu from '@databases/context-menu/select-field-menu';
import FieldName from '@databases/field-name/FieldName';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { type DatabaseField } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import composeRefs from '@seznam/compose-react-refs';
import React, { type FunctionComponent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { useEditAiSummaryField } from '../database-content/ai-config/use-edit-ai-summary-field.ts';

const fieldTriggerStyles = xcss({
  alignItems: 'center',
  backgroundColor: 'color.background.neutral.subtle',
  borderRadius: 'radius.medium',
  boxSizing: 'border-box',
  color: 'color.text.subtle',
  display: 'flex',
  height: '42px',
  paddingBlock: 'space.0',
  paddingInline: 'space.100',
  textAlign: 'start',

  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered'
  }
});

const openFieldTriggerStyles = xcss({
  backgroundColor: 'color.background.neutral.subtle.hovered'
});

const fieldNameStyles = xcss({
  flexGrow: 1,
  minWidth: '0px',
  overflow: 'hidden'
});

const hiddenChevronStyles = xcss({
  display: 'inline-flex',
  flexShrink: 0,
  opacity: '0'
});

const visibleChevronStyles = xcss({
  opacity: '1'
});

interface EntryDetailsFieldActionsProps {
  contentId: string;
  currentViewId: string;
  field: DatabaseField;
  viewSettings: DatabaseViewSettings;
}

interface EntryDetailsFieldTriggerProps {
  field: DatabaseField;
  isOpen: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  triggerProps?: TriggerProps;
}

interface EntryDetailsFieldChevronProps {
  isEnabled: boolean;
  isVisible: boolean;
}

const EntryDetailsFieldChevron: FunctionComponent<EntryDetailsFieldChevronProps> = ({
  isEnabled,
  isVisible
}) => {
  if (!isEnabled) {
    return null;
  }

  return (
    <Box
      as="span"
      data-is-visible={isVisible ? 'true' : 'false'}
      testId="entry-details-field-chevron"
      xcss={[hiddenChevronStyles, isVisible && visibleChevronStyles]}
    >
      <ChevronDownIcon label="" size="small" />
    </Box>
  );
};

const EntryDetailsFieldTrigger: FunctionComponent<EntryDetailsFieldTriggerProps> = ({
  field,
  isOpen,
  onClick,
  triggerProps
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { formatMessage } = useIntl();
  const { ref, ...buttonProps } = triggerProps ?? {};
  const isChevronVisible = isHovered || isFocused || isOpen;
  const fieldName = field.name || formatMessage(messages['field.default-field-name']);

  return (
    <TruncatedTooltip content={fieldName} position="left">
      {({
        ref: tooltipRef,
        onBlur: onTooltipBlur,
        onClick: onTooltipClick,
        onFocus: onTooltipFocus,
        ...tooltipTriggerProps
      }) => (
        <Pressable
          {...buttonProps}
          {...tooltipTriggerProps}
          aria-expanded={triggerProps ? isOpen : undefined}
          aria-haspopup={triggerProps ? 'menu' : undefined}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onBlur={(event) => {
            onTooltipBlur(event);
            setIsFocused(false);
          }}
          onClick={(event) => {
            onTooltipClick(event);
            onClick(event);
          }}
          onFocus={(event) => {
            onTooltipFocus(event);
            setIsFocused(true);
          }}
          ref={composeRefs(
            ref as React.Ref<HTMLButtonElement>,
            tooltipRef as React.Ref<HTMLButtonElement>
          )}
          testId="entry-details-field-trigger"
          type="button"
          xcss={[fieldTriggerStyles, isOpen && openFieldTriggerStyles]}
        >
          <Box xcss={fieldNameStyles}>
            <FieldName field={field} iconSpacing="space.100" isBold showIcon />
          </Box>
          <EntryDetailsFieldChevron
            isEnabled={Boolean(triggerProps)}
            isVisible={isChevronVisible}
          />
        </Pressable>
      )}
    </TruncatedTooltip>
  );
};

const ContextualEntryDetailsFieldActions: FunctionComponent<
  EntryDetailsFieldActionsProps
> = ({ contentId, currentViewId, field, viewSettings }) => {
  const handleEditAiSummaryField = useEditAiSummaryField({
    contentId,
    currentViewId,
    fieldId: field.id
  });
  const renderEntryPanelTrigger = useCallback(
    (
      triggerProps: TriggerProps,
      {
        isOpen,
        onClick
      }: {
        isOpen: boolean;
        onClick: React.MouseEventHandler<HTMLButtonElement>;
      }
    ) => (
      <EntryDetailsFieldTrigger
        field={field}
        isOpen={isOpen}
        onClick={onClick}
        triggerProps={triggerProps}
      />
    ),
    [field]
  );

  return (
    <SelectFieldMenu
      canEditFields
      contentId={contentId}
      currentViewId={currentViewId}
      fieldId={field.id}
      onEditAiSummaryField={handleEditAiSummaryField}
      renderEntryPanelTrigger={renderEntryPanelTrigger}
      surface="entry-panel"
      viewSettings={viewSettings}
    />
  );
};

const LegacyEntryDetailsFieldActions: FunctionComponent<
  EntryDetailsFieldActionsProps
> = ({ contentId, field }) => {
  const client = useAnalytics();
  const { showPanel } = usePanel();
  const handleClick = useCallback(() => {
    showPanel('FieldSettingsPanel', { fieldId: field.id });
    void client.sendDatabaseFieldSettingsOpenedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: 'fieldSettingsPanel',
      fieldType: field.type
    });
  }, [client, contentId, field.id, field.type, showPanel]);

  return <EntryDetailsFieldTrigger field={field} isOpen={false} onClick={handleClick} />;
};

const EntryDetailsFieldActions: FunctionComponent<EntryDetailsFieldActionsProps> = (
  props
) => {
  const isContextualMenusM2Enabled = getExperimentValue(
    'databasesContextualMenusM2',
    'enabled',
    { fireExperimentExposure: false }
  );

  return isContextualMenusM2Enabled ? (
    <ContextualEntryDetailsFieldActions {...props} />
  ) : (
    <LegacyEntryDetailsFieldActions {...props} />
  );
};

export default EntryDetailsFieldActions;
